import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../components/ui/PageLayout';
import { useSnackbar } from '../contexts/SnackbarContext';

import { log } from '../utils/errors/logger';
import { getProviderIcon, getProviderColor } from '../utils/documents/documentSourceHelpers';
import { formatDate } from '../utils/formatting/formatters';
import {
  useAdminProviderConfigs,
  useCreateProviderConfig,
  useUpdateProviderConfig,
  useDeleteProviderConfig,
} from '../hooks/api/useDocumentSources';

interface DocumentSourceProviderConfig {
  id: string;
  provider_type: 'onedrive' | 'dropbox' | 'nextcloud';
  name: string;
  description?: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface ProviderConfigRequest {
  provider_type: 'onedrive' | 'dropbox' | 'nextcloud';
  name: string;
  description?: string;
  enabled: boolean;
  onedrive_config?: {
    client_id: string;
    client_secret: string;
    tenant_id: string;
    redirect_uri: string;
    scope: string;
  };
  dropbox_config?: {
    app_key: string;
    app_secret: string;
    redirect_uri: string;
  };
  nextcloud_config?: {
    server_url: string;
    username: string;
    password: string;
    webdav_path: string;
  };
}

const DocumentSourceConfig: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning, showInfo } = useSnackbar();
  const { data: providers = [], isLoading } = useAdminProviderConfigs();
  const createProviderConfig = useCreateProviderConfig();
  const updateProviderConfig = useUpdateProviderConfig();
  const deleteProviderConfig = useDeleteProviderConfig();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<DocumentSourceProviderConfig | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<DocumentSourceProviderConfig | null>(null);

  // Form state
  const [formData, setFormData] = useState<ProviderConfigRequest>({
    provider_type: 'onedrive',
    name: '',
    description: '',
    enabled: true,
  });

  const [oneDriveConfig, setOneDriveConfig] = useState({
    client_id: '',
    client_secret: '',
    tenant_id: '',
    redirect_uri: 'http://localhost:8000/auth/onedrive/callback',
    scope: 'https://graph.microsoft.com/Files.ReadWrite.All',
  });

  const [dropboxConfig, setDropboxConfig] = useState({
    app_key: '',
    app_secret: '',
    redirect_uri: 'http://localhost:8000/auth/dropbox/callback',
  });

  const [nextcloudConfig, setNextcloudConfig] = useState({
    server_url: '',
    username: '',
    password: '',
    webdav_path: '/remote.php/dav/files/',
  });

  const handleOpenDialog = (provider?: DocumentSourceProviderConfig) => {
    if (provider) {
      setEditingProvider(provider);
      setFormData({
        provider_type: provider.provider_type,
        name: provider.name,
        description: provider.description || '',
        enabled: provider.enabled,
      });
      // Note: We don't load existing config for security reasons
    } else {
      setEditingProvider(null);
      setFormData({
        provider_type: 'onedrive',
        name: '',
        description: '',
        enabled: true,
      });
      // Reset config forms
      setOneDriveConfig({
        client_id: '',
        client_secret: '',
        tenant_id: '',
        redirect_uri: 'http://localhost:8000/auth/onedrive/callback',
        scope: 'https://graph.microsoft.com/Files.ReadWrite.All',
      });
      setDropboxConfig({
        app_key: '',
        app_secret: '',
        redirect_uri: 'http://localhost:8000/auth/dropbox/callback',
      });
      setNextcloudConfig({
        server_url: '',
        username: '',
        password: '',
        webdav_path: '/remote.php/dav/files/',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProvider(null);
  };

  const handleSaveProvider = async () => {
    try {
      const requestData = { ...formData };

      // Add provider-specific config
      if (formData.provider_type === 'onedrive') {
        requestData.onedrive_config = oneDriveConfig;
      } else if (formData.provider_type === 'dropbox') {
        requestData.dropbox_config = dropboxConfig;
      } else if (formData.provider_type === 'nextcloud') {
        requestData.nextcloud_config = nextcloudConfig;
      }

      if (editingProvider) {
        await updateProviderConfig.mutateAsync({ configId: editingProvider.id, data: requestData });
        showSuccess(t('documentSourceConfig.messages.saveSuccess'));
      } else {
        await createProviderConfig.mutateAsync(requestData);
        showSuccess(t('documentSourceConfig.messages.saveSuccess'));
      }

      handleCloseDialog();
    } catch (error) {
      log.error('Error saving provider', { error });
      showError(t('documentSourceConfig.messages.saveFailed'));
    }
  };

  const handleDeleteProvider = async () => {
    if (!providerToDelete) { return; }

    try {
      await deleteProviderConfig.mutateAsync(providerToDelete.id);
      showSuccess(t('documentSourceConfig.messages.deleteSuccess'));
      setDeleteConfirmOpen(false);
      setProviderToDelete(null);
    } catch (error: any) {
      log.error('Error deleting provider', { error });

      // Check if the error is due to active connections
      if (error?.response?.status === 400 && error?.response?.data?.error_code === 'ACTIVE_CONNECTIONS_EXIST') {
        const activeCount = error?.response?.data?.active_connections || 0;
        const errorMsg = t('documentSourceConfig.messages.deleteFailedActiveConnections', { count: activeCount });
        showError(errorMsg);
      } else if (error?.response?.data?.message) {
        // Use the server's error message if available
        showError(error.response.data.message);
      } else {
        // Generic error message
        showError(t('documentSourceConfig.messages.deleteFailed'));
      }

      setDeleteConfirmOpen(false);
      setProviderToDelete(null);
    }
  };

  const formatProviderType = (type: string | undefined | null): string => {
    if (!type) {
      return 'Unknown';
    }
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        title={t('documentSourceConfig.title')}
        headerActions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('documentSourceConfig.addProvider')}
          </Button>
        }
        isLoading={isLoading}
        maxWidth="xl"
      >
        {providers.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {t('documentSourceConfig.noProviders')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {providers.map((provider) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={provider.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: getProviderColor(provider.provider_type), mr: 2 }}>
                        {getProviderIcon(provider.provider_type)}
                      </Avatar>
                      <Box flexGrow={1}>
                        <Typography variant="h6">{provider.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatProviderType(provider.provider_type)}
                        </Typography>
                      </Box>
                      <Chip
                        label={provider.enabled ? t('common.enabled') : t('common.disabled')}
                        color={provider.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    {provider.description && (
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        {provider.description}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      {t('common.createdAt')}: {formatDate(provider.created_at, i18n.language)}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(provider)}
                      color="primary"
                      title={t('documentSourceConfig.editProvider')}
                      aria-label={t('documentSourceConfig.editProvider')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setProviderToDelete(provider);
                        setDeleteConfirmOpen(true);
                      }}
                      color="error"
                      title={t('documentSourceConfig.deleteProvider')}
                      aria-label={t('documentSourceConfig.deleteProvider')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </PageLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProvider
            ? t('documentSourceConfig.editProvider')
            : t('documentSourceConfig.addProvider')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={12} key="provider-type">
                <FormControl fullWidth>
                  <InputLabel>{t('documentSourceConfig.providerType')}</InputLabel>
                  <Select
                    value={formData.provider_type}
                    onChange={(e) => setFormData({ ...formData, provider_type: e.target.value as any })}
                    disabled={Boolean(editingProvider)}
                  >
                    <MenuItem value="onedrive">OneDrive</MenuItem>
                    <MenuItem value="dropbox">Dropbox</MenuItem>
                    <MenuItem value="nextcloud">Nextcloud</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12} key="name">
                <TextField
                  fullWidth
                  label={t('common.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>

              <Grid size={12} key="description">
                <TextField
                  fullWidth
                  label={t('common.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid size={12} key="enabled">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enabled}
                      onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    />
                  }
                  label={t('common.enabled')}
                />
              </Grid>

              <Grid size={12} key="config-divider">
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('documentSourceConfig.providerConfiguration')}
                </Typography>
              </Grid>

              {/* OneDrive Configuration */}
              {formData.provider_type === 'onedrive' && (
                <>
                  <Grid size={12} key="onedrive-client-id">
                    <TextField
                      fullWidth
                      label="Client ID"
                      value={oneDriveConfig.client_id}
                      onChange={(e) => setOneDriveConfig({ ...oneDriveConfig, client_id: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.onedrive.clientIdHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="onedrive-client-secret">
                    <TextField
                      fullWidth
                      label="Client Secret"
                      type="password"
                      value={oneDriveConfig.client_secret}
                      onChange={(e) => setOneDriveConfig({ ...oneDriveConfig, client_secret: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.onedrive.clientSecretHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="onedrive-tenant-id">
                    <TextField
                      fullWidth
                      label="Tenant ID"
                      value={oneDriveConfig.tenant_id}
                      onChange={(e) => setOneDriveConfig({ ...oneDriveConfig, tenant_id: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.onedrive.tenantIdHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="onedrive-redirect-uri">
                    <TextField
                      fullWidth
                      label="Redirect URI"
                      value={oneDriveConfig.redirect_uri}
                      onChange={(e) => setOneDriveConfig({ ...oneDriveConfig, redirect_uri: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.onedrive.redirectUriHelper')}
                    />
                  </Grid>
                </>
              )}

              {/* Dropbox Configuration */}
              {formData.provider_type === 'dropbox' && (
                <>
                  <Grid size={12} key="dropbox-app-key">
                    <TextField
                      fullWidth
                      label="App Key"
                      value={dropboxConfig.app_key}
                      onChange={(e) => setDropboxConfig({ ...dropboxConfig, app_key: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.dropbox.appKeyHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="dropbox-app-secret">
                    <TextField
                      fullWidth
                      label="App Secret"
                      type="password"
                      value={dropboxConfig.app_secret}
                      onChange={(e) => setDropboxConfig({ ...dropboxConfig, app_secret: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.dropbox.appSecretHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="dropbox-redirect-uri">
                    <TextField
                      fullWidth
                      label="Redirect URI"
                      value={dropboxConfig.redirect_uri}
                      onChange={(e) => setDropboxConfig({ ...dropboxConfig, redirect_uri: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.dropbox.redirectUriHelper')}
                    />
                  </Grid>
                </>
              )}

              {/* Nextcloud Configuration */}
              {formData.provider_type === 'nextcloud' && (
                <>
                  <Grid size={12} key="nextcloud-server-url">
                    <TextField
                      fullWidth
                      label="Server URL"
                      value={nextcloudConfig.server_url}
                      onChange={(e) => setNextcloudConfig({ ...nextcloudConfig, server_url: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.nextcloud.serverUrlHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="nextcloud-username">
                    <TextField
                      fullWidth
                      label="Username"
                      value={nextcloudConfig.username}
                      onChange={(e) => setNextcloudConfig({ ...nextcloudConfig, username: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.nextcloud.usernameHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="nextcloud-password">
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={nextcloudConfig.password}
                      onChange={(e) => setNextcloudConfig({ ...nextcloudConfig, password: e.target.value })}
                      required
                      helperText={t('documentSourceConfig.nextcloud.passwordHelper')}
                    />
                  </Grid>
                  <Grid size={12} key="nextcloud-webdav-path">
                    <TextField
                      fullWidth
                      label="WebDAV Path"
                      value={nextcloudConfig.webdav_path}
                      onChange={(e) => setNextcloudConfig({ ...nextcloudConfig, webdav_path: e.target.value })}
                      helperText={t('documentSourceConfig.nextcloud.webdavPathHelper')}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveProvider} variant="contained" color="primary">
            {editingProvider ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('documentSourceConfig.confirmDeleteProvider', {
              name: providerToDelete?.name || ''
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteProvider} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentSourceConfig;